import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | error

  useEffect(() => {
    document.title = "Verify email — PodiumOne";
    let done = false;
    (async () => {
      if (!token) { setStatus("error"); return; }
      try {
        await api.get("/auth/verify-email", { params: { token } });
        if (!done) { setStatus("success"); refreshUser && refreshUser(); }
      } catch {
        if (!done) setStatus("error");
      }
    })();
    return () => { done = true; };
  }, [token, refreshUser]);

  return (
    <div>
      <Navbar />
      <div className="max-w-lg mx-auto px-4 pt-40 pb-24 text-center" data-testid="verify-email-page">
        {status === "loading" && (
          <><Loader2 className="w-10 h-10 mx-auto animate-spin text-white/40 mb-6" /><p className="text-white/60">Activating your account…</p></>
        )}
        {status === "success" && (
          <div data-testid="verify-success">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-emerald-500/15 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">Account activated!</h1>
            <p className="text-white/60 leading-relaxed mb-8">Your <span className="text-white font-semibold">$10.00</span> and <span className="text-white font-semibold">RM10.00</span> credits have been added.</p>
            <Button data-testid="verify-login-btn" onClick={() => navigate("/login")} className="rounded-full h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold active:scale-95 transition-all">
              Sign in &amp; start bidding
            </Button>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-red-500/15 flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">Invalid or expired link</h1>
            <p className="text-white/60 leading-relaxed mb-8">This verification link is no longer valid. It may have already been used.</p>
            <Button data-testid="verify-error-login-btn" onClick={() => navigate("/login")} variant="outline" className="rounded-full border-white/15 hover:bg-white/10">Back to sign in</Button>
          </div>
        )}
      </div>
    </div>
  );
}
